import { EMPTY_RECORD } from "@enfusion-ui/core";
import { useMounted, useRefCallback } from "@enfusion-ui/hooks";
import { LoadReportParams } from "@enfusion-ui/types";
import {
  createReportColumnDefs,
  errorToast,
  ReportContextMetaStoreEntry,
  ReportContextRowsStoreEntry,
  ReportDataChangeHandler,
  TabDef,
  useTabs,
} from "@enfusion-ui/web-core";
import { useWorkerModule } from "@enfusion-ui/web-workers";
import React from "react";
import { v4 as uuidv4 } from "uuid";

import { SocketData } from "../../types/socket";
import { useInspectorContext } from "../inspector/context";
import {
  ReportContextDataStoreEntry,
  ReportOpenDetailTableArgs,
  ReportsContext,
} from "./context";

export type ReportsProviderMessage = {
  command:
    | "init-status"
    | "table-sync"
    | "rows-update"
    | "row-metadata"
    | "row-delete"
    | "error"
    | "load-report"
    | "update-open-subscriptions"
    | "current-state"
    | "metadata-error";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
};

export const ReportsProvider: React.FC = ({ children }) => {
  const { subscribeToModule, postMessage: postMessageBase } =
    useWorkerModule("socket");

  const {
    config: { username },
    apiSource,
    environment,
  } = useInspectorContext();

  const postMessage = useRefCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (payload: Record<string, any>) => {
      return postMessageBase({
        command: "msg",
        type: "report",
        apiSource,
        apiBase: environment.restAPIUrl.split("://")[1],
        payload,
      });
    },
    [postMessageBase, apiSource, environment.restAPIUrl]
  );

  const [messages, setMessages] = React.useState<SocketData[]>([]);

  const [metaStore, setMetaStore] = React.useState<
    Record<string, ReportContextMetaStoreEntry>
  >({});
  const [dataStore, setDataStore] = React.useState<
    Record<string, ReportContextDataStoreEntry>
  >({});

  const rowsStoreRef = React.useRef<
    Record<string, ReportContextRowsStoreEntry>
  >({});
  const dataStoreRef = React.useRef<
    Record<string, ReportContextDataStoreEntry>
  >({});
  const changeSubscriptionsRef = React.useRef<
    Record<string, Array<ReportDataChangeHandler>>
  >({});

  const isMounted = useMounted();
  const { onTabClose } = useTabs();
  const [reportId, setReportId] = React.useState("");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const constructDataStore = (tableData: any) => {
    const { tableId, totalsRow, tableMetadata, rows } = tableData;
    rowsStoreRef.current = {
      ...rowsStoreRef.current,
      [tableId]: {
        rows,
        totalsRow,
      },
    };

    dataStoreRef.current = {
      ...dataStoreRef.current,
      [tableId]: {
        columnDefs: createReportColumnDefs(tableData),
        rows,
        totalsRow,
        metadata: {
          ...tableMetadata,
          columns: tableMetadata.columns.reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (res: any, entry: any) => ({ ...res, [entry.name]: entry }),
            {}
          ),
          descriptionColumn: tableMetadata.columns.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (i: any) => i.visible === true
          )?.name,
        },
      },
    };
  };

  React.useEffect(() => {
    return subscribeToModule((msg: Record<string, any>) => {
      const apiBase = environment.restAPIUrl.split("://")[1];
      if (
        msg.type === "report" &&
        msg.apiBase === apiBase &&
        msg.apiSource === apiSource
      ) {
        if (msg.command === "passthrough") {
          const { command, payload } = msg.payload as ReportsProviderMessage;
          switch (command) {
            case "init-status": {
              setMetaStore(payload.metaStore);
              break;
            }
            case "table-sync": {
              constructDataStore(payload.tableData);

              setMetaStore(payload.metaStore);
              setDataStore(dataStoreRef.current);

              const { tableId } = payload.tableData;
              requestAnimationFrame(() => {
                if (changeSubscriptionsRef.current[tableId]) {
                  for (const handler of changeSubscriptionsRef.current[
                    tableId
                  ]) {
                    handler({
                      change: {
                        add: rowsStoreRef.current[tableId].rows,
                      },
                      totalsRow: rowsStoreRef.current[tableId].totalsRow,
                      tableId,
                      columnDefs: dataStoreRef.current[tableId].columnDefs,
                      type: "init",
                    } as any);
                  }
                }
              });
              break;
            }
            case "rows-update": {
              const { tableId, totalsRow, newRows, add } = payload;

              rowsStoreRef.current = {
                ...rowsStoreRef.current,
                [tableId]: {
                  ...rowsStoreRef.current[tableId],
                  rows: newRows,
                  totalsRow: { ...totalsRow },
                },
              };

              if (changeSubscriptionsRef.current[tableId]) {
                for (const handler of changeSubscriptionsRef.current[tableId]) {
                  handler({
                    change: {
                      add,
                      update: newRows.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (i: any) =>
                          !add.some((a: any) => a.__row_id === i.__row_id)
                      ),
                    },
                    totalsRow,
                    tableId,
                  });
                }
              }

              setMetaStore(payload.metaStore);
              setDataStore((prevState: any) => {
                return {
                  ...prevState,
                  [tableId]: {
                    ...prevState[tableId],
                    rows: newRows,
                    totalsRow,
                  },
                };
              });
              break;
            }

            case "row-delete": {
              const { newRows, tableId, rowIndex } = payload;

              rowsStoreRef.current = {
                ...rowsStoreRef.current,
                [tableId]: {
                  ...rowsStoreRef.current[tableId],
                  rows: newRows,
                },
              };

              const remove = newRows.splice(rowIndex, 1);

              if (changeSubscriptionsRef.current[tableId]) {
                for (const handler of changeSubscriptionsRef.current[tableId]) {
                  handler({ change: { remove, update: newRows }, tableId });
                }
              }
              setMetaStore(payload.metaStore);
              setDataStore((prevState: any) => {
                return {
                  ...prevState,
                  [tableId]: {
                    ...prevState[tableId],
                    rows: newRows,
                  },
                };
              });
              break;
            }
            case "error": {
              console.warn("syncError", payload.message);
              errorToast(payload.message);
              break;
            }
          }
        } else if (msg.command === "socket-log") {
          setMessages((cur) => [...cur, msg.payload as SocketData]);
        }
      }
    });
  }, [subscribeToModule, apiSource, environment.restAPIUrl]);

  //TODO: Handle on tab close to unsubscribe the report
  React.useEffect(() => {
    const unsubscribeOnTabClose = onTabClose((tabDef: TabDef) => {
      if (tabDef.component === "report" && isMounted()) {
        closeReport(tabDef.config.reportId, tabDef.config.reportId);
      }
    });

    return unsubscribeOnTabClose;
  }, []);

  const initializeReports = useRefCallback(
    (secureToken?: string) => {
      postMessageBase({
        command: "open",
        type: "report",
        apiSource,
        apiBase: environment.restAPIUrl.split("://")[1],
      });
      postMessage({
        command: "socket-open",
        payload: { userName: username, passCode: secureToken },
      });
    },
    [username, postMessage]
  );

  const openDetailTable = useRefCallback(
    ({
      destination,
      parentTableId,
      row,
      tableId = uuidv4(),
    }: ReportOpenDetailTableArgs) => {
      let params;
      if (parentTableId !== "") {
        params = {
          tableId,
          rowId: row,
          parentTableId,
        };
      } else {
        params = {
          tableId,
          rowId: row,
        };
      }
      postMessage({
        command: "open-detail-table",
        payload: {
          destination,
          reportId,
          params,
        },
      });
    },
    [postMessage]
  );

  const preLoadRef = React.useRef<Record<string, number | undefined>>({});

  const loadReport = useRefCallback(
    ({
      reportId,
      name,
      path: pathBase,
      params: allParams = EMPTY_RECORD,
      closeFirst = true,
      reportQuery,
    }: LoadReportParams) => {
      preLoadRef.current[reportId] = setTimeout(() => {
        try {
          const allParamKeys = Object.keys(allParams);
          const params = allParamKeys.reduce((res, key) => {
            if (!key.startsWith("url.")) {
              res[key] = allParams[key];
            }
            return res;
          }, {} as Record<string, any>);
          setReportId(reportId);
          const payload = {
            reportId,
            name,
            path: pathBase,
            reportQuery,
            params,
          };
          if (closeFirst) {
            setMetaStore((prev) => ({
              ...prev,
              [reportId]: {
                ...prev[reportId],
                progressSteps: undefined,
                loading: true,
              },
            }));
            closeReport(reportId, params.tableId);
          }

          postMessage({
            command: "load-report",
            payload,
          });
        } catch (err: any) {
          console.log("loadReport error", err);
          errorToast(err.message ? err?.message : err);
        }
      }, 300) as unknown as number;
    },
    [postMessage]
  );

  const closeReport = useRefCallback(
    (reportIdToClose: string, tableIdToClose: string) => {
      postMessage({
        command: "close-report",
        payload: {
          reportIdToClose,
          tableIdToClose,
        },
      });

      postMessage({
        command: "unsubscribe",
        payload: {
          reportIdToClose,
        },
      });
    },
    [postMessage]
  );

  const subscribeToReportChange = useRefCallback(
    (tableId: string, callback: ReportDataChangeHandler) => {
      const subs = (changeSubscriptionsRef.current[tableId] || []).filter(
        (i) => i !== callback
      );

      changeSubscriptionsRef.current = {
        ...changeSubscriptionsRef.current,
        [tableId]: [...subs, callback],
      };

      return () => {
        const subs = (changeSubscriptionsRef.current[tableId] || []).filter(
          (i) => i !== callback
        );

        changeSubscriptionsRef.current = {
          ...changeSubscriptionsRef.current,
          [tableId]: subs,
        };
      };
    },
    []
  );

  return (
    <ReportsContext.Provider
      value={{
        dataStore,
        metaStore,
        messages,
        reportId,
        loadReport,
        closeReport,
        subscribeToReportChange,
        openDetailTable,
        initializeReports,
      }}
    >
      {children}
    </ReportsContext.Provider>
  );
};
